(ns frontend.view.modals.email-editor.view
  (:require
   [clojure.data :as data]
   [clojure.string :as string]
   [common.email :as email]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.registry :as schema-registry]
   [common.util :as cu]
   [frontend.actions.person :as actions-person]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.state.association :as association]
   [frontend.state.current-session :as session]
   [frontend.utils :as fu]
   [frontend.view.components.select :as select-comp]
   [frontend.view.entity :as f-entity]
   [frontend.view.entity-viewers.events.editor-utils :as editor-utils]
   [frontend.view.modals.attachments.view :as attachments]
   [frontend.view.modals.entity-editor.view :as entity-editor-modal]
   [frontend.view.modals.events :as modals]
   [reagent.core :as r]
   [ui.components.alert.view :as alert]
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [ui.components.rich-text.tiptap :as tiptap]
   [ui.components.toggle.view :as toggle]
   [ui.dom-globals :as dg]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.util :as uu]))

(defonce modal-key :email-editor-modal)

(defn editor-view
  [editor-state]
  (let [rich-text-state (r/atom {:body (:email/body @editor-state)})]
    (fn [editor-state]
      [:div
       {:class "min-h-[300px] flex p-0 flex-auto"}
       [tiptap/editor
        {:value             (or (:body @rich-text-state)
                                (:email/body @editor-state)
                                "")
         :class             "w-full flex flex-col"
         :on-change         (fn [value]
                              (when-let [old-timeout-handle (:timeout @rich-text-state)]
                                (js/clearTimeout old-timeout-handle)
                                (swap! rich-text-state dissoc :timeout))
                              (swap! rich-text-state assoc :body value)
                              (let [t-handle (js/setTimeout (fn []
                                                              (swap! editor-state assoc
                                                                     :email/body
                                                                     (:body @rich-text-state))
                                                              (swap! rich-text-state dissoc :timeout))
                                                            1000)]
                                (swap! rich-text-state assoc :timeout t-handle)))
         :show-alignment?   true
         :show-block-type?  false
         :link?             false
         :inline-options    [:bold :italic :underline]}]])))

(defn has-email?
  [entity]
  (let [entity-type (:entity/type entity)]
    (case entity-type
      :entity.type/person (email/valid-email-pattern? (:person/universal-email-address entity))
      :entity.type/account (email/valid-email-pattern? (:account/email entity))
      (do (logging/error "Unexpected entity type as email recipient : " entity)
          false))))

(defn render-recipient-option
  [select-val props]
  (let [{:keys [:value
                :isSelected
                :isFocused
                :label]}
        props
        entity-id (-> props
                      (js->clj :keywordize true)
                      :value
                      (first))
        entity (get @session/entities entity-id)
        selected? (or isSelected
                      (and (coll? select-val)
                           (some #{entity-id} select-val))
                      (= select-val entity-id))
        entity-type (:entity/type entity)
        avatar-url (:person/avatar-url entity)
        avatar-url (fu/only-https-or-relative-img avatar-url)
        picture-url (:account/picture-url entity)
        picture-url (fu/only-https-or-relative-img picture-url)
        has-email? (has-email? entity)]
    [:div
     (merge
      {:class-name (fu/cs :select-option__contact
                          (when selected?
                            :is-selected)
                          (when isFocused
                            :is-focused)
                          (when-not has-email?
                            :email-missing)
                          (when (= :entity.type/account entity-type)
                            :account))}
      (:innerProps (js->clj props)))
     [:span
      {:class-name (fu/cs :select-option__contact__circle
                          (when-not has-email?
                            :email-missing))}
      (cond
        (= :entity.type/person entity-type) (cond
                                              avatar-url
                                              [:img
                                               {:src        (fu/sanitize-img-url avatar-url)
                                                :class-name "avatar-image"}]

                                              (or (:person/first-name entity)
                                                  (:person/last-name entity))
                                              [:span
                                               (str (first (:person/first-name entity))
                                                    ""
                                                    (first (:person/last-name entity)))]

                                              :else
                                              [:i.fal.fa-at])
        (= :entity.type/account entity-type) (cond
                                               picture-url
                                               [:img
                                                {:src        (fu/sanitize-img-url picture-url)
                                                 :class-name "avatar-image"}]

                                               :else
                                               [:span
                                                (str (first (:account/first-name entity))
                                                     ""
                                                     (first (:account/last-name entity)))]))]
     [:span.select-option__contact__label.ml-2
      (or (:person/universal-email-address entity)
          (:account/email entity)
          label
          (->> value
               :label)
          (->> value
               :new-value))]]))

(defn render-empty-component [_props])

(defn recipient-input
  [_args]
  (let [field-atom (r/atom {})]
    (fn
      [{:keys [:value
               :on-save!
               :placeholder
               :attribute
               :related-entity
               :email]
        :as   args}]
      (let [related-entity-id (:db/id related-entity)
            related-groups (into {}
                                 (association/resolve-related-entities-by-group
                                  related-entity
                                  :entity.type/person))
            related-contact-associations (:association.type/contact-person related-groups)]
        [select-comp/select-creatable
         {:is-multi            true
          :value               value
          :placeholder         (when (empty? value)
                                 placeholder)
          :key                 (some-> args
                                       :key-atom
                                       deref
                                       str)
          :on-change!
          (fn [args]
            (let [diff (data/diff (set value) (set args))
                  addition (and (coll? diff) (second diff))
                  removal (and (coll? diff) (first diff))
                  removed-id (and (coll? removal) (first removal))
                  added-id (and (coll? addition) (first addition))
                  added-entity (when added-id (get @session/entities added-id))
                  related-before (:common/related-entities email)]
              (when (and removed-id (some #{removed-id} related-before))
                (on-save! :common/related-entities (disj (set related-before) removed-id)))
              (if (or (not added-id)
                      (has-email? added-entity))
                (do (when (and added-id (= :entity.type/person (:entity/type added-entity)))
                      (on-save! :common/related-entities (conj (set related-before) added-id)))
                    (on-save! attribute (set args)))
                (modals/open-modal!
                 entity-editor-modal/modal-key
                 {:entity-id         added-id
                  :skip-success-msg? true
                  :ok-button-text    (loc/tr :done)
                  :mandatory-fields  [:person/universal-email-address]
                  :on-processed!     (fn [{:keys [_new-entity-id close-cb!]}]
                                       (js/setTimeout close-cb! 2000))
                  :on-ok!            (fn [_new-entity-id close-cb!]
                                       (on-save! attribute (set args))
                                       (on-save! :common/related-entities
                                                 (conj (set related-before) added-id))
                                       (close-cb!))}))))
          :escape-clears-value true
          :ref                 (fn [component]
                                 (when (and component
                                            (not= component (:select-component @field-atom)))
                                   (swap! field-atom assoc :select-component component)))
          :loading-placeholder " "                                                                  ;loading-text
          :auto-blur           false
          :components          {:Option             (r/reactify-component (partial render-recipient-option value))
                                :DropdownIndicator  (r/reactify-component render-empty-component)
                                :IndicatorSeparator (r/reactify-component render-empty-component)
                                :ClearIndicator     (r/reactify-component render-empty-component)}
          :get-option-value    (fn [args]
                                 args)
          :get-option-label    (fn [eid-or-new-option]
                                 (let [cla (js->clj eid-or-new-option :keywordize-keys true)
                                       resolve-option (fn [cla]
                                                        (cond
                                                          (:new-value cla) (:label cla)
                                                          (string? cla) cla
                                                          (number? cla) (let [entity (get @session/entities eid-or-new-option)]
                                                                          (or (:person/universal-email-address entity)
                                                                              (:account/email entity)))
                                                          (and (coll? cla)
                                                               (= (count cla) 2)
                                                               (second cla))
                                                          (second cla)
                                                          :else
                                                          (logging/error "Could not resolve email select-creatable value "
                                                                         cla)))
                                       res (if (set? cla)
                                             (->> cla
                                                  (map resolve-option)
                                                  (set))
                                             (resolve-option cla))]
                                   res))
          :default-options     true
          :is-valid-new-option (fn [args]
                                 (let [trimmed (cu/str-trim-lcase args)]
                                   (and (email/valid-email-pattern? trimmed)
                                        (not (some #{trimmed} (:new-options @field-atom))))))
          :get-new-option-data (fn [args]
                                 (let [trimmed (cu/str-trim-lcase args)]
                                   {:new-value  trimmed
                                    :icon       (fn [_data]
                                                  [:i.fal.fa-plus-circle.create-option__icon])
                                    :class-name "create-option select-dropdown-menu-button"
                                    :label      (loc/tr :create-email-recipient [trimmed])}))
          :value-type          :entity
          :entity-types        [:entity.type/person]
          :on-create-option    (fn [_args]
                                 (f-entity/open-creator!
                                  :entity.type/person
                                  {:on-ok!           (fn [{:keys [new-entity-id close-cb!]}]
                                                       (on-save! attribute (conj (set value) new-entity-id))
                                                       (close-cb!))
                                   :mandatory-fields [:person/universal-email-address]}))
          ;        :on-create-option    (fn [args]
          ;                               (on-save! attribute (concat value [args]))
          ;                               (swap! field-atom assoc :new-options (concat (:new-options @field-atom)
          ;                                                                            [args])))
          :load-select-options
          (partial
           select-comp/load-options
           {:temp-atom field-atom
            :search-function
            (fn [input-lc results-cb]
              (let [contact-options
                    (->> related-contact-associations
                         (map (fn [c]
                                (let [target-id (:entity-association/target-entity c)
                                      source-id (:entity-association/source-entity c)
                                      person (get @session/entities
                                                  (if (= target-id related-entity-id)
                                                    source-id
                                                    target-id))]
                                  [(:db/id person) (entity/display-string person :long)]))))
                    active-account-options (->> (session/active-accounts nil)
                                                (map (fn [a]
                                                       [(:db/id a)
                                                        (entity/display-string a :long)])))]
                (if (empty? input-lc)
                  (results-cb (concat contact-options active-account-options))
                  (results-cb
                   (take 50
                         (select-comp/get-options
                          {:value-type        :entity
                           :entity-types      [:entity.type/person]
                           :entities-atom     session/entities
                           :entity-types-atom session/entity-types
                           :has-focus?        (:has-focus? @field-atom)
                           :search-str-lc     input-lc
                           :filter-fn         (select-comp/filter-person-option
                                               {:query-mandatory-attributes
                                                [:person/universal-email-address]
                                                :query-attributes
                                                [:person/universal-email-address
                                                 :person/first-name
                                                 :person/last-name]})}))))))})}]))))

(defn header-block
  [_state _view-state _entity subject]
  (let [header-atom (r/atom {:subject subject})]
    (fn [state view-state entity]
      (let [cc-visible? (or (:cc-visible? @view-state)
                            (not-empty (:email/cc @state)))
            bcc-visible? (or (:bcc-visible? @view-state)
                             (not-empty (:email/bcc @state)))
            on-save-attr (fn [attr v]
                           (swap! state assoc attr v))
            subject (or (:subject @header-atom)
                        (:email/subject @state)
                        "")]
        [:div
         {:class "pb-3 relative"}
         (when-not (and cc-visible? bcc-visible?)
           [:div
            {:class "flex gap-2 absolute z-100 top-2 right-2"}
            (when-not cc-visible?
              [:span
               {:class    "cursor-pointer hover:underline"
                :on-click (fn [_]
                            (swap! view-state assoc :cc-visible? true))}
               (loc/tr :cc)])
            (when-not bcc-visible?
              [:span
               {:class    "cursor-pointer hover:underline"
                :on-click (fn [_]
                            (swap! view-state assoc :bcc-visible? true))}
               (loc/tr :bcc)])])
         [recipient-input
          {:value          (:email/to @state)
           :validate!      (fn [_attr _v]
                             (logging/unimplemented " Email validate not implemented "))
           :on-save!       on-save-attr
           :attribute      :email/to
           :placeholder    (loc/tr :email/to)
           :error-text     (->> @state
                                :errors
                                :email/to)
           :related-entity entity
           :email          @state}]
         (when cc-visible?
           [recipient-input
            {:value          (:email/cc @state)
             :validate!      (fn [_attr _v]
                               (logging/unimplemented " Email validate not implemented "))
             :on-save!       on-save-attr
             :attribute      :email/cc
             :placeholder    (loc/tr :email/cc)
             :error-text     (->> @state
                                  :errors
                                  :email/cc)
             :related-entity entity
             :email          @state}])
         (when bcc-visible?
           [recipient-input
            {:value          (:email/bcc @state)
             :validate!      (fn [_attr _v]
                               (logging/unimplemented " Email validate not implemented "))
             :on-save!       on-save-attr
             :attribute      :email/bcc
             :placeholder    (loc/tr :email/bcc)
             :error-text     (->> @state
                                  :errors
                                  :email/bcc)
             :related-entity entity
             :email          @state}])

         [labelled-input/view
          {:placeholder (loc/tr :email/subject)
           :data-type   :data.type/text-simple
           :value       subject
           :on-blur     (fn [_]
                          (when-let [new-subject (:subject @header-atom)]
                            (swap! state assoc :email/subject new-subject))
                          (swap! header-atom dissoc :subject))
           :on-change   (fn [value]
                          (swap! header-atom assoc
                                 :subject
                                 value))}]]))))

(defn associations-view
  [state show?]
  (let [tenant-schema @session/tenant-schema
        meta-params {:local-atom        state
                     :entity-atom       state
                     :entity-type       :related-entities
                     :metadata-template (editor-utils/resolve-activity-template
                                         {:raw-schema (merge (schema-registry/get-schema
                                                              tenant-schema
                                                              :related-entities)
                                                             (schema-registry/get-schema
                                                              tenant-schema
                                                              :entity.type/activity))})
                     :update-draft!     (fn [k v & _rest]
                                          (swap! state assoc k v))}]
    [:div
     {:class (uu/cm "flex-1 px-2" (when-not show? "hidden"))}
     [editor-utils/metadata-view meta-params]]))

(defn body-block
  [state view-state]
  (let [show-assoc? (:associations-visible? @view-state)]
    [:div
     {:class "w-full flex gap-2 flex-auto relative overflow-auto"}
     [editor-view state]
     [associations-view state show-assoc?]]))

(defn attachments-block
  [state]
  (let [attachments (:common/attachments @state)]
    (when (> (count attachments) 0)
      [:ul
       {:class "pt-3 mt-3 flex gap-2 flex-wrap border-t border-solid border-base-light-border-paler"}
       (doall (for [id attachments]
                ^{:key (str "email-editor_attachment-" id)}
                [:li
                 {:class "p-1.5 flex items-center gap-2 bg-complement-light rounded"}
                 [:i.fal.fa-paperclip]
                 [:span (entity/display-string (or (get @session/entities id) id))]
                 [:i.fal.fa-times
                  {:class    "text-xs cursor-pointer hover:text-base-light-text-accent"
                   :on-click (fn [_]
                               (swap! state assoc
                                      :common/attachments
                                      (->> @state
                                           :common/attachments
                                           (remove (fn [aid]
                                                     (= id aid)))
                                           (set))))}]]))])))

(defn resolve-recipient
  [email-address-str]
  (let [l-case (string/lower-case (string/trim (str email-address-str)))]
    (or (->> (session/active-accounts nil)
             (filter (fn [a]
                       (= l-case (string/lower-case (string/trim (str (:account/email a)))))))
             (first))
        (->> @session/all-persons
             (filter
              (fn [p]
                (= l-case
                   (string/lower-case (string/trim (str (:person/universal-email-address p)))))))
             (first)))))

(defn resolve-recipient-ids
  [email-addresses-str]
  (let [recipient-id-or-emails (->> email-addresses-str
                                    (email/parse-email-addresses)
                                    (map (fn [e]
                                           (let [entity (resolve-recipient e)]
                                             (if entity
                                               (:db/id entity)
                                               e)))))]
    [(->> recipient-id-or-emails
          (filter number?)
          (into []))
     (->> recipient-id-or-emails
          (remove number?)
          (into []))]))

(defn view
  [{:keys [props state]}]
  (let [view-state (r/cursor state [:view-state])
        initial-body (:body props)
        initial-attachments (:attachments props)
        _ (when (or initial-body initial-attachments)
            (swap! state assoc
                   :email/body (or initial-body nil)
                   :common/attachments (or initial-attachments #{})))
        initialize (fn [subject body related-entities attachments entity-id to cc bcc]
                     (let [privacy-status (or (-> @session/account
                                                  :account/default-nylas-privacy)
                                              :privacy-status/public)
                           init (fn [init-to init-cc init-bcc]
                                  (reset! state (merge
                                                 {:common/related-entities (or related-entities
                                                                               (when entity-id #{entity-id}))
                                                  :email/privacy-status    privacy-status}
                                                 (when init-to
                                                   {:email/to (set init-to)})
                                                 (when init-cc
                                                   {:email/cc (set init-cc)})
                                                 (when init-bcc
                                                   {:email/bcc (set init-bcc)})
                                                 (when subject
                                                   {:email/subject subject})
                                                 (when body
                                                   {:email/body body})
                                                 (when attachments
                                                   {:common/attachments attachments}))))
                           [to-recipient-ids to-unknown-emails] (resolve-recipient-ids to)
                           [cc-recipient-ids cc-unknown-emails] (resolve-recipient-ids cc)
                           [bcc-recipient-ids bcc-unknown-emails] (resolve-recipient-ids bcc)
                           all-unknown (concat to-unknown-emails cc-unknown-emails bcc-unknown-emails)]
                       (if (empty? all-unknown)
                         (init to-recipient-ids cc-recipient-ids bcc-recipient-ids)
                         (actions-person/*->create-many
                          {:emails all-unknown}
                          (fn [resp]
                            (let [mappings (:body resp)
                                  resolved-to (concat to-recipient-ids
                                                      (->> to-unknown-emails
                                                           (select-keys mappings)
                                                           (vals)))
                                  resolved-cc (concat cc-recipient-ids
                                                      (->> cc-unknown-emails
                                                           (select-keys mappings)
                                                           (vals)))
                                  resolved-bcc (concat bcc-recipient-ids
                                                       (->> bcc-unknown-emails
                                                            (select-keys mappings)
                                                            (vals)))]
                              (init resolved-to resolved-cc resolved-bcc)))
                          (fn [_err]
                            (init to-recipient-ids cc-recipient-ids bcc-recipient-ids)
                            (logging/error "Failed to create unknown emails"))))))
        {:keys [related-entities entity-id to cc bcc subject body]} props
        attachments (:common/attachments @state)]
    (js/setTimeout
     #(initialize subject body related-entities attachments entity-id to cc bcc)
     500)
    (fn [{:keys [shown? close-modal! props state topmost?]}]
      (let [{:keys [title-text on-primary-submit!]} props
            entity (get @session/entities entity-id)
            attachments (:common/attachments @state)
            alert (:alert @state)
            show-assoc? (:associations-visible? @view-state)
            assoc-count (count (:common/related-entities @state))
            valid-email? (and (not (cu/blank-value? (:email/to @state)))
                              (not (cu/blank-value? (:email/subject @state)))
                              (not (cu/blank-value? (:email/body @state))))]
        [modal/view
         {:visible?      (and shown? topmost?)
          :hide!         close-modal!
          :backdrop      "static"
          :close-button? true}
         title-text
         [:<>
          (when alert
            [:div
             [alert/danger
              {:on-close #(swap! state dissoc :alert)}
              [:div
               alert]
              [:div
               (loc/tr :nylas-reconnect-info)
               [:a
                {:href     (routing/path-for :personal-profile)
                 :on-click (fn [e]
                             (dg/prevent-and-stop e)
                             (routing/set-view! [:personal-profile :tab-key :my-details])
                             (close-modal!))}
                (loc/tr :settings/account-settings)]]]])

          [header-block state view-state entity subject]

          [body-block state view-state]
          [attachments-block state]
          ]

         (if (:processing? @view-state)
           [:i.fal.fa-spinner.fa-pulse.fa-2x]
           [:<>
            [:div
             {:class "w-full flex justify-between items-center"}
             [:div
              {:class "flex gap-6 items-center"}
              [:div
               {:class "flex gap-2 items-center"}
               [:div
                {:class "flex items-center gap-1"}
                [toggle/toggle
                 {:checked?  (= :privacy-status/private (:email/privacy-status @state))
                  :disabled? false
                  :on-change (fn [checked?]
                               (swap! state assoc :email/privacy-status (if checked?
                                                                          :privacy-status/private
                                                                          :privacy-status/public)))}]
                [:span
                 {:class "block min-w-10 text-xs"}
                 (if (= :privacy-status/private (:email/privacy-status @state))
                   "Private"
                   "Public")]]

               [button/secondary-open
                {:on-click (fn [_e]
                             (close-modal!))}
                [:i.fal.fa-trash-alt]]
               [button/secondary-open
                {:on-click (fn [_e]
                             (modals/open-modal!
                              attachments/modal-key
                              {:entity-attachments attachments
                               :on-primary-submit! (fn [files]
                                                     (swap! state assoc
                                                            :common/attachments
                                                            (set (concat (:common/attachments @state)
                                                                         files))))}))}
                [:i.fal.fa-paperclip]]]
              [button/secondary-open
               {:class    "text-sm"
                :on-click (fn [_]
                            (swap! view-state assoc :associations-visible? (not show-assoc?)))}
               (cond
                 (= assoc-count 0) (loc/tr :email/associations-toggle-none)
                 (= assoc-count 1) (loc/tr :email/associations-with-1-entity)
                 :else (loc/tr :email/associations-toggle-multiple [assoc-count]))
               [:i
                {:class-name (fu/cs :fal
                                    (if show-assoc? :fa-chevron-up :fa-chevron-down))}]]]
             [modal/right-single-button
              nil
              (not valid-email?)
              (fn [_]
                (swap! view-state assoc :processing? true)
                (on-primary-submit!
                 (dissoc @state :view-state :alert)
                 (fn [args]
                   (swap! view-state dissoc :processing?)
                   (close-modal! args))
                 (fn [error]
                   (swap! view-state dissoc :processing?)
                   (let [error-data (ex-data error)
                         msg (:message error-data)]
                     (swap! state assoc :alert (loc/tr :failed-to-send-email [msg]))))))
              [:<>
               [:i.fal.fa-paper-plane]
               [:span
                (loc/tr :create-email-button)]]
              nil]]])]))))
